import { useState, type ReactNode } from "react";

import { EmptyState, Input, Pagination, Stack, Toolbar } from "@/components/ui";

export interface PagedProps<T> {
  readonly rows: readonly T[];
  readonly pageSize?: number;
  readonly placeholder?: string;
  readonly match?: (row: T, needle: string) => boolean;
  readonly action?: ReactNode;
  readonly children: (page: T[]) => ReactNode;
}

/** Any list that can be long: a filter, one page, and the pager. Nothing renders whole. */
export function Paged<T>({ rows, pageSize, placeholder, match, action, children }: PagedProps<T>) {
  const size = pageSize ?? 10;
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const needle = query.trim().toLowerCase();
  const matched =
    needle !== "" && match !== undefined ? rows.filter((row) => match(row, needle)) : rows;
  const pageCount = Math.max(1, Math.ceil(matched.length / size));
  const current = Math.min(page, pageCount);
  const slice = matched.slice((current - 1) * size, current * size);

  return (
    <Stack gap={4}>
      {match === undefined ? null : (
        <Toolbar fill>
          <Input
            value={query}
            placeholder={placeholder ?? "Filter"}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
          />
          {action}
        </Toolbar>
      )}
      {slice.length > 0 ? children(slice) : <EmptyState bare headline="No matches" />}
      {matched.length > size ? (
        <Pagination
          page={current}
          pageCount={pageCount}
          pageSize={size}
          total={matched.length}
          label="Page"
          onChange={setPage}
        />
      ) : null}
    </Stack>
  );
}
